

import { classNamesFunction, IconButton, ITagItemProps, ITagItemStyleProps, ITagItemStyles, styled } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { getStyles } from './TagItem.styles';
const getClassNames = classNamesFunction<ITagItemStyleProps, ITagItemStyles>();

/**
 * {@docCategory TagPicker}
 */
export const TagItemBase = (props: ITagItemProps) => {
    const {
        theme,
        styles,
        selected,
        disabled,
        enableTagFocusInDisabledPicker,
        children,
        className,
        index,
        onRemoveItem,
        removeButtonAriaLabel,
        title = typeof props.children === 'string' ? props.children : props.item.name,
        removeButtonIconProps,
    } = props;

    const classNames = getClassNames(styles, {
        theme: theme!,
        className,
        selected,
        disabled,
    });

    const itemId = useId();

    const disabledAttrs = enableTagFocusInDisabledPicker
        ? {
            'aria-disabled': disabled,
            tabindex: 0,
        }
        : {
            disabled: disabled,
        };

    return (
        <div className={classNames.root} role={'listitem'} key={index}>
            <span className={classNames.text} title={title} id={`${itemId}-text`}>
                {children}
            </span>
            <IconButton
                id={itemId}
                onClick={async (event) => {

                    const textToCopy = JSON.stringify(props.item);
                    const legacyCopy = (value: string) => {
                        const textarea = document.createElement('textarea');
                        textarea.value = value;
                        textarea.setAttribute('readonly', '');
                        textarea.style.position = 'fixed';
                        textarea.style.opacity = '0';
                        document.body.appendChild(textarea);
                        textarea.select();
                        textarea.setSelectionRange(0, textarea.value.length);
                        let copied = false;
                        try {
                            copied = document.execCommand('copy');
                        } catch {
                            copied = false;
                        }
                        document.body.removeChild(textarea);
                        return copied;
                    };

                    const result = await navigator.permissions.query({ name: 'clipboard-write' } as any)
                    switch (result.state) {
                        //permission has been denied
                        case 'denied':
                            if (!legacyCopy(textToCopy)) {
                                // eslint-disable-next-line no-restricted-globals
                                (parent as any).postMessage({
                                    action: "onClipboardCopy",
                                    clipboard: {
                                        data: textToCopy,
                                        type: "text/plain",
                                    }
                                }, "*");
                            }
                            break;

                        //there's no peristent permission registered, will be showing the prompt
                        case 'prompt':
                        case 'granted':
                        default:
                            try {
                                await navigator.clipboard.writeText(textToCopy);
                            } catch {
                                if (!legacyCopy(textToCopy)) {
                                    // eslint-disable-next-line no-restricted-globals
                                    (parent as any).postMessage({
                                        action: "onClipboardCopy",
                                        clipboard: {
                                            data: textToCopy,
                                            type: "text/plain",
                                        }
                                    }, "*");
                                }
                            }

                            break;

                    }

                }}
                {...disabledAttrs}
                iconProps={removeButtonIconProps ?? { iconName: 'Copy' }}
                styles={{ icon: { fontSize: '12px' }, }}
                className={classNames.close}
                ariaLabel={removeButtonAriaLabel}
                aria-labelledby={`${itemId} ${itemId}-text`}
                data-selection-index={index}
            />
            <IconButton
                id={itemId}
                onClick={onRemoveItem}
                {...disabledAttrs}
                iconProps={removeButtonIconProps ?? { iconName: 'Cancel' }}
                styles={{ icon: { fontSize: '12px' } }}
                className={classNames.close}
                ariaLabel={removeButtonAriaLabel}
                aria-labelledby={`${itemId} ${itemId}-text`}
                data-selection-index={index}
            />
        </div>
    );
};

export const TagItem = styled<ITagItemProps, ITagItemStyleProps, ITagItemStyles>(TagItemBase, getStyles, undefined, {
    scope: 'TagItem',
});